
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BookOpen, MessageSquare, ChevronDown, ArrowUp, ClipboardList, Users, Rocket, Printer, ArrowLeft } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Link } from 'react-router-dom';
import LanguageSelector from '@/components/LanguageSelector';
import SEO from '@/components/SEO';

interface Slide {
  id: number;
  title: string;
  content: string[];
  deepDive?: string[];
  questions?: string[];
}

const useSlides = (): Slide[] => {
  const { t } = useTranslation('course');

  return Array.from({ length: 10 }, (_, i) => {
    const deepDive = t(`slides.${i}.deepDive`, { returnObjects: true, defaultValue: null });
    const questions = t(`slides.${i}.questions`, { returnObjects: true, defaultValue: null });

    return {
      id: i,
      title: t(`slides.${i}.title`),
      content: t(`slides.${i}.content`, { returnObjects: true }) as string[],
      deepDive: Array.isArray(deepDive) ? deepDive : undefined,
      questions: Array.isArray(questions) ? questions : undefined,
    };
  });
};






const handlePrintSlide = (slide: Slide, t: (key: string, options?: any) => string) => {
  const w = window.open('', '_blank');
  if (!w) return;

  const taskPattern = /^(Uppgift|Analys|Fältstudie|Mini-övning|Spåra beroenden|Exit-plan|Rollspel|Individuell plan|Värderingskarta|Task|Analysis|Field study|Mini-exercise|Trace dependencies|Exit plan|Role play|Individual plan|Values map):/;
  const discussionPattern = /^(Diskussion|Discussion):/;
  const actionPattern = /^(Handling|Action):/;

  const formatQuestions = (questions: string[]) =>
    questions.map((q) => {
      let label = '';
      let icon = '';
      if (taskPattern.test(q)) { label = 'task'; icon = '📋'; }
      else if (discussionPattern.test(q)) { label = 'discussion'; icon = '👥'; }
      else if (actionPattern.test(q)) { label = 'action'; icon = '🚀'; }
      else { return `<p class="intro">${q}</p>`; }
      return `<div class="material-card ${label}"><span class="icon">${icon}</span><p>${q}</p></div>`;
    }).join('');

  w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>${t('ui.printSection')} ${slide.id}: ${slide.title}</title>
<style>
  *{margin:0;padding:0;box-sizing:border-box}
  body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif;color:#1a2a3a;max-width:700px;margin:0 auto;padding:40px 24px;line-height:1.7}
  h1{font-family:Georgia,'Times New Roman',serif;font-size:28px;margin-bottom:4px;color:#003049}
  h2{font-family:Georgia,'Times New Roman',serif;font-size:20px;margin:32px 0 12px;color:#003049;border-bottom:2px solid #669bbc;padding-bottom:6px}
  p{margin-bottom:12px;font-size:15px}
  .subtitle{font-size:14px;color:#669bbc;margin-bottom:24px}
  .intro{font-style:italic;margin-bottom:12px}
  .material-card{display:flex;gap:12px;padding:14px 16px;border-radius:8px;border:1px solid #e0e0e0;margin-bottom:10px;page-break-inside:avoid}
  .material-card .icon{font-size:20px;flex-shrink:0}
  .material-card p{margin:0}
  .material-card.task{border-color:#669bbc;background:#f4f9fc}
  .material-card.discussion{border-color:#c1121f;background:#fdf5f5}
  .material-card.action{border-color:#c1121f;background:#fff8f0}
  @media print{body{padding:20px}h2{break-after:avoid}}
</style></head><body>
<h1>${t('ui.printSection')} ${slide.id}: ${slide.title}</h1>
<p class="subtitle">${t('ui.printSubtitle')}</p>

<h2>${t('ui.printCourseText')}</h2>
${slide.content.map(p => `<p>${p}</p>`).join('')}

${slide.deepDive ? `<h2>${t('ui.printDeepDive')}</h2>${slide.deepDive.map(p => `<p>${p}</p>`).join('')}` : ''}

${slide.questions ? `<h2>${t('ui.printMaterial')}</h2>${formatQuestions(slide.questions)}` : ''}

</body></html>`);
  w.document.close();
  setTimeout(() => w.print(), 400);
};

const SlideSection: React.FC<{
  slide: Slide;
  index: number;
  isVisible: boolean;
  onOpenDeepDive: () => void;
  onOpenQuestions: () => void;
  t: (key: string, options?: any) => string;
}> = ({ slide, index, isVisible, onOpenDeepDive, onOpenQuestions, t }) => {
  return (
    <section
      className={`min-h-screen flex items-center justify-center relative transition-opacity duration-700 ${
        isVisible ? 'opacity-100' : 'opacity-40'
      }`}
    >
      <div className="container-custom max-w-3xl py-20 md:py-32">
        {/* Slide number */}
        <div className="mb-6 flex items-center gap-3">
          <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-secondary text-secondary-foreground text-sm font-bold">
            {index}
          </span>
          <div className="h-px flex-1 bg-border" />
        </div>

        {/* Title */}
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-8 leading-tight">
          {slide.title}
        </h2>

        {/* Content paragraphs */}
        <div className="space-y-6">
          {slide.content.map((paragraph, pIdx) => (
            <p
              key={pIdx}
              className="text-lg md:text-xl leading-relaxed text-muted-foreground"
            >
              {paragraph}
            </p>
          ))}
        </div>

        {/* Action buttons */}
        <div className="mt-10 flex flex-col sm:flex-row gap-3">
          <button
            onClick={onOpenDeepDive}
            className="inline-flex items-center gap-2 px-5 py-3 rounded-lg border border-border bg-card text-card-foreground hover:bg-muted transition-colors text-sm font-medium"
          >
            <BookOpen className="h-4 w-4 text-accent" />
            {t('ui.deepDive')}
          </button>
          <button
            onClick={onOpenQuestions}
            className="inline-flex items-center gap-2 px-5 py-3 rounded-lg border border-border bg-card text-card-foreground hover:bg-muted transition-colors text-sm font-medium"
          >
            <MessageSquare className="h-4 w-4 text-primary" />
            {t('ui.material')}
          </button>
          <button
            onClick={() => handlePrintSlide(slide, t)}
            className="inline-flex items-center gap-2 px-5 py-3 rounded-lg border border-border bg-card text-card-foreground hover:bg-muted transition-colors text-sm font-medium"
          >
            <Printer className="h-4 w-4 text-muted-foreground" />
            {t('ui.print')}
          </button>
        </div>

        {/* Scroll hint (only on first slide) */}
        {index === 0 && (
          <div className="mt-16 flex justify-center animate-bounce">
            <ChevronDown className="h-6 w-6 text-muted-foreground" />
          </div>
        )}
      </div>
    </section>
  );
};

const Course = () => {
  const { t } = useTranslation('course');
  const slides = useSlides();
  const [visibleSlides, setVisibleSlides] = useState<Set<number>>(new Set([0]));
  const [currentSlide, setCurrentSlide] = useState(0);
  const [deepDiveOpen, setDeepDiveOpen] = useState(false);
  const [questionsOpen, setQuestionsOpen] = useState(false);
  const [activeSlide, setActiveSlide] = useState<Slide | null>(null);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const sectionRefs = useRef<(HTMLDivElement | null)[]>([]);

  const dotLabels = t('dotLabels', { returnObjects: true }) as string[];

  // Intersection observer for slide visibility
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        setVisibleSlides((prev) => {
          const next = new Set(prev);
          entries.forEach((entry) => {
            const idx = Number(
              (entry.target as HTMLElement).dataset.slideIndex
            );
            if (entry.isIntersecting) {
              next.add(idx);
              setCurrentSlide(idx);
            } else {
              next.delete(idx);
            }
          });
          return next;
        });
      },
      { threshold: 0.3 }
    );

    sectionRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  // Arrow key navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (deepDiveOpen || questionsOpen) return;

      if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
        e.preventDefault();
        const next = Math.min(currentSlide + 1, slides.length - 1);
        sectionRefs.current[next]?.scrollIntoView({ behavior: 'smooth' });
      } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
        e.preventDefault();
        const prev = Math.max(currentSlide - 1, 0);
        sectionRefs.current[prev]?.scrollIntoView({ behavior: 'smooth' });
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentSlide, deepDiveOpen, questionsOpen, slides.length]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const handleScroll = () => {
      setShowScrollTop(el.scrollTop > window.innerHeight);
    };
    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, []);

  // Progress indicator
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const handleScroll = () => {
      const scrollTop = el.scrollTop;
      const scrollHeight = el.scrollHeight - el.clientHeight;
      setProgress(scrollHeight > 0 ? (scrollTop / scrollHeight) * 100 : 0);
    };
    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="h-screen overflow-y-auto snap-y snap-mandatory bg-background" ref={containerRef}>
      <SEO
        title={t('seo.title')}
        description={t('seo.description')}
        url="/kurs"
      />

      {/* Top bar with back link and language selector */}
      <div className="fixed top-0 left-0 right-0 z-50">
        <div className="h-1 bg-muted">
          <div
            className="h-full bg-primary transition-all duration-150 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="flex items-center justify-between px-4 md:px-8 py-2 bg-background/95 backdrop-blur-sm border-b border-border/50">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Frikopplad.nu
          </Link>
          <LanguageSelector />
        </div>
      </div>

      {/* Side progress dots */}
      <nav className="fixed right-4 md:right-8 top-1/2 -translate-y-1/2 z-40 hidden md:flex flex-col gap-2">
        {slides.map((_, idx) => (
          <div key={idx} className="relative group flex items-center justify-end">
            <span className="absolute right-5 mr-2 px-2 py-1 rounded bg-secondary text-secondary-foreground text-xs font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
              {dotLabels[idx]}
            </span>
            <button
              onClick={() =>
                sectionRefs.current[idx]?.scrollIntoView({ behavior: 'smooth' })
              }
              className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                visibleSlides.has(idx)
                  ? 'bg-primary scale-125'
                  : 'bg-border hover:bg-muted-foreground'
              }`}
              aria-label={t('ui.goTo', { name: dotLabels[idx] })}
            />
          </div>
        ))}
      </nav>

      {/* Slides */}
      {slides.map((slide, idx) => (
        <div
          key={slide.id}
          ref={(el) => (sectionRefs.current[idx] = el)}
          data-slide-index={idx}
          className="snap-start"
        >
          <SlideSection
            slide={slide}
            index={idx}
            isVisible={visibleSlides.has(idx)}
            onOpenDeepDive={() => {
              setActiveSlide(slide);
              setDeepDiveOpen(true);
            }}
            onOpenQuestions={() => {
              setActiveSlide(slide);
              setQuestionsOpen(true);
            }}
            t={t}
          />
        </div>
      ))}

      {/* Deep Dive Dialog */}
      <Dialog open={deepDiveOpen} onOpenChange={setDeepDiveOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-accent" />
              {t('ui.deepDiveTitle', { title: activeSlide?.title })}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {activeSlide?.deepDive ? (
              <div className="space-y-4">
                {activeSlide.deepDive.map((text, i) => (
                  <p key={i} className="text-muted-foreground leading-relaxed">{text}</p>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground italic">
                {t('ui.deepDiveEmpty')}
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Material Dialog */}
      <Dialog open={questionsOpen} onOpenChange={setQuestionsOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-primary" />
              {t('ui.materialTitle', { title: activeSlide?.title })}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {activeSlide?.questions ? (
              <div className="space-y-3">
                {activeSlide.questions.map((q, i) => {
                  const isTask = /^(Uppgift|Analys|Fältstudie|Mini-övning|Spåra beroenden|Exit-plan|Rollspel|Individuell plan|Värderingskarta|Task|Analysis|Field study|Mini-exercise|Trace dependencies|Exit plan|Role play|Individual plan|Values map):/.test(q);
                  const isDiscussion = /^(Diskussion|Discussion):/.test(q);
                  const isAction = /^(Handling|Action):/.test(q);

                  let icon = null;
                  let borderColor = 'border-border';
                  if (isTask) {
                    icon = <ClipboardList className="h-5 w-5 text-accent shrink-0 mt-0.5" />;
                    borderColor = 'border-accent/30';
                  } else if (isDiscussion) {
                    icon = <Users className="h-5 w-5 text-primary shrink-0 mt-0.5" />;
                    borderColor = 'border-primary/30';
                  } else if (isAction) {
                    icon = <Rocket className="h-5 w-5 text-frikopplad-red shrink-0 mt-0.5" />;
                    borderColor = 'border-frikopplad-red/30';
                  }

                  if (icon) {
                    return (
                      <div key={i} className={`flex gap-3 p-4 rounded-lg border ${borderColor} bg-card`}>
                        {icon}
                        <p className="text-muted-foreground leading-relaxed">{q}</p>
                      </div>
                    );
                  }

                  return (
                    <p key={i} className="text-muted-foreground leading-relaxed italic">{q}</p>
                  );
                })}
              </div>
            ) : (
              <p className="text-muted-foreground italic">
                {t('ui.materialEmpty')}
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Scroll to top */}
      {showScrollTop && (
        <button
          onClick={() => containerRef.current?.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-6 right-6 z-40 p-3 rounded-full bg-secondary text-secondary-foreground shadow-lg hover:bg-secondary/90 transition-all"
          aria-label={t('ui.backToTop')}
        >
          <ArrowUp className="h-5 w-5" />
        </button>
      )}
    </div>
  );
};

export default Course;
